/**
 * 内容安全管理 API 服务
 *
 * 封装内容安全管理相关的 REST API 调用，用于管理后台 /admin/safe 页面。
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * 管理 API 请求封装：附带用户体系 Bearer token（is_admin 用户认证兜底），
 * 后端优先校验 admin_token / access_token Cookie
 */
async function adminFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers || {});
  const userToken =
    sessionStorage.getItem('access_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('access_token');
  if (userToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${userToken}`);
  }
  return fetch(input, { ...init, headers, credentials: init.credentials ?? 'include' });
}

// ========================================
// 类型定义
// ========================================

export interface SafeguardOverviewStats {
  total_checks_24h: number;
  blocked_count_24h: number;
  warned_count_24h: number;
  high_severity_count_24h: number;
  cloud_check_count_24h: number;
  unique_users_flagged_24h: number;
  banned_users_count: number;
  top_matched_words: Array<{ word: string; count: number }>;
  top_categories: Array<{ category: string; count: number }>;
  source_distribution: Array<{ source: string; count: number }>;
}

export interface SafeguardTrendItem {
  date: string;
  total: number;
  blocked: number;
  warned: number;
}

export interface HighRiskUser {
  id: number;
  email: string;
  nickname: string | null;
  status: string;
  violation_count: number;
  last_violation_at: string | null;
  banned_reason: string | null;
}

export interface AuditLogItem {
  id: number;
  created_at: string | null;
  check_type: string;
  check_direction: string;
  matched_words: string[];
  categories: string[];
  severity: string;
  sensitive_level: string | null;
  action: string;
  source_endpoint: string | null;
  session_id: string | null;
  user_id: number | null;
  trigger_content: string | null;
  alibaba_risk_level: string | null;
  alibaba_response_code: number | null;
  user_email: string | null;
  user_nickname: string | null;
}

export interface AuditLogQueryResult {
  total: number;
  items: AuditLogItem[];
  limit: number;
  offset: number;
}

export interface AuditLogDetail extends AuditLogItem {
  content_hash: string | null;
  alibaba_request_id: string | null;
}

export interface UserProfile {
  user_id: number;
  email: string;
  nickname: string | null;
  status: string;
  violation_count: number;
  banned_at: string | null;
  banned_until: string | null;
  banned_reason: string | null;
  total_checks: number;
  blocked_count: number;
  warned_count: number;
  high_severity_count: number;
  first_violation_at: string | null;
  last_violation_at: string | null;
  category_distribution: Array<{ category: string; count: number }>;
  violation_timeline: Array<{
    id: number;
    created_at: string | null;
    action: string;
    severity: string;
    matched_words: string[];
    source_endpoint: string | null;
    check_direction: string;
  }>;
  ban_history: Array<{
    id: number;
    action_type: string;
    ban_type: string;
    duration_hours: number;
    reason: string | null;
    created_at: string | null;
  }>;
  created_at: string | null;
}

export interface BannedUser {
  id: number;
  email: string;
  nickname: string | null;
  banned_at: string | null;
  banned_until: string | null;
  banned_reason: string | null;
  violation_count: number;
  is_permanent: boolean;
  remaining_hours: number | null;
}

export interface BanRecord {
  id: number;
  user_id: number;
  admin_id: number | null;
  action_type: string;
  ban_type: string;
  duration_hours: number;
  reason: string | null;
  auto_trigger_count: number | null;
  created_at: string | null;
  user_email: string | null;
  user_nickname: string | null;
}

export interface SafeguardSettings {
  auto_ban_enabled: boolean;
  auto_ban_threshold: number;
  auto_ban_duration_hours: number;
  violation_reset_days: number;
  local_enabled: boolean;
  cloud_enabled: boolean;
  audit_log_enabled: boolean;
  log_retention_days: number;
  trigger_content_enabled: boolean;
}

// ========================================
// 通用请求工具
// ========================================

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.detail || errorData.message || '请求失败';
    if (response.status === 401) throw new Error(`未授权: ${message}`);
    if (response.status === 403) throw new Error(`没有权限: ${message}`);
    if (response.status === 404) throw new Error(`资源不存在: ${message}`);
    throw new Error(message);
  }
  return response.json();
}

// ========================================
// API 客户端
// ========================================

export const safeguardAdminApi = {
  // ========== 统计概览 ==========

  async getOverviewStats(): Promise<SafeguardOverviewStats> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/safeguard/stats/overview`, {
      credentials: 'include',
    });
    return handleResponse<SafeguardOverviewStats>(response);
  },

  async getTrends(days: number = 7): Promise<SafeguardTrendItem[]> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/stats/trends?days=${days}`,
      { credentials: 'include' }
    );
    return handleResponse<SafeguardTrendItem[]>(response);
  },

  async getHighRiskUsers(limit: number = 20): Promise<HighRiskUser[]> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/stats/high-risk-users?limit=${limit}`,
      { credentials: 'include' }
    );
    return handleResponse<HighRiskUser[]>(response);
  },

  // ========== 审核日志 ==========

  async queryLogs(params: {
    severity?: string;
    action?: string;
    source_endpoint?: string;
    user_id?: number;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<AuditLogQueryResult> {
    const searchParams = new URLSearchParams();
    if (params.severity) searchParams.set('severity', params.severity);
    if (params.action) searchParams.set('action', params.action);
    if (params.source_endpoint) searchParams.set('source_endpoint', params.source_endpoint);
    if (params.user_id) searchParams.set('user_id', String(params.user_id));
    if (params.start_date) searchParams.set('start_date', params.start_date);
    if (params.end_date) searchParams.set('end_date', params.end_date);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));

    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/logs?${searchParams.toString()}`,
      { credentials: 'include' }
    );
    return handleResponse<AuditLogQueryResult>(response);
  },

  async getLogDetail(logId: number): Promise<AuditLogDetail> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/logs/${logId}`,
      { credentials: 'include' }
    );
    return handleResponse<AuditLogDetail>(response);
  },

  // ========== 用户画像 ==========

  async getUserProfile(userId: number): Promise<UserProfile> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/users/${userId}/profile`,
      { credentials: 'include' }
    );
    return handleResponse<UserProfile>(response);
  },

  // ========== 封禁管理 ==========

  async getBannedUsers(): Promise<BannedUser[]> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/users/banned`,
      { credentials: 'include' }
    );
    return handleResponse<BannedUser[]>(response);
  },

  async banUser(userId: number, durationHours: number, reason: string): Promise<Record<string, unknown>> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/users/${userId}/ban`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ duration_hours: durationHours, reason }),
      }
    );
    return handleResponse<Record<string, unknown>>(response);
  },

  async unbanUser(userId: number, reason: string): Promise<Record<string, unknown>> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/users/${userId}/unban`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason }),
      }
    );
    return handleResponse<Record<string, unknown>>(response);
  },

  async getBanRecords(params?: { user_id?: number; limit?: number; offset?: number }): Promise<BanRecord[]> {
    const searchParams = new URLSearchParams();
    if (params?.user_id) searchParams.set('user_id', String(params.user_id));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/ban-records?${searchParams.toString()}`,
      { credentials: 'include' }
    );
    return handleResponse<BanRecord[]>(response);
  },

  // ========== 安全配置 ==========

  async getSettings(): Promise<SafeguardSettings> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/settings`,
      { credentials: 'include' }
    );
    return handleResponse<SafeguardSettings>(response);
  },

  async updateSettings(settings: Partial<SafeguardSettings>): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/safeguard/settings`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings),
      }
    );
    return handleResponse<{ success: boolean; message: string }>(response);
  },
};

export default safeguardAdminApi;
